import { ValidationError } from '@/models/api'
import { ValidationErrors } from '@/models/store'

/**
 * Parses errors into the simplified object.
 *
 * @param errors An array of errors received from the backend
 * @returns {Object} Parsed into the object errors to simplify future use.
 */
export function parseValidationErrors<FieldTypes extends string = string>(
  errors: ValidationError<FieldTypes>[]
): ValidationErrors<FieldTypes> {
  return errors.reduce<ValidationErrors<FieldTypes>>((acc, current) => {
    acc[current.field] = current.message
    return acc
  }, {})
}

/**
 * Axios gives a request that never got a response a technical message
 * ("Network Error", "timeout of 30000ms exceeded"), so it is replaced with
 * one an admin can act on.
 */
function getNoResponseError(error: unknown): string | undefined {
  const err = error as any
  if (err?.response) return undefined

  if (err?.code === 'ECONNABORTED') {
    return 'The server did not respond in time. Check your internet connection and try again.'
  }

  if (err?.message === 'Network Error') {
    return 'No connection to the server. Check your internet connection and try again.'
  }

  return undefined
}

export interface ParseErrorsOutput<FieldTypes extends string> {
  error: string
  errorStatus: number
  errorCode: string
  validationErrors?: ValidationErrors<FieldTypes>
  validationErrorsRaw?: ValidationError[]
}

/**
 * Extracts a human-friendly error message from an API error (Axios error, Error, string, etc.).
 * Prioritizes backend explanation, validation messages, and converts raw HTTP status messages
 * into human-readable text instead of technical "Request failed with status code XXX".
 */
export function extractErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred'
): string {
  if (!error) return fallback

  if (typeof error === 'string') {
    return error.trim() || fallback
  }

  const err = error as any

  // 1. Validation errors array from backend (Adonis Validator format)
  const validationErrors = err?.response?.data?.errors as
    | ValidationError[]
    | undefined
  if (
    Array.isArray(validationErrors) &&
    validationErrors.length > 0 &&
    validationErrors[0]?.message
  ) {
    return String(validationErrors[0].message).trim()
  }

  // 2. Direct message string from backend response
  const resMessage = err?.response?.data?.message
  if (typeof resMessage === 'string' && resMessage.trim()) {
    return resMessage.trim()
  }

  // 3. Error field if string from backend response
  const resError = err?.response?.data?.error
  if (typeof resError === 'string' && resError.trim()) {
    return resError.trim()
  }

  // 4. Connection or timeout issues
  const noResponse = getNoResponseError(error)
  if (noResponse) return noResponse

  // 5. Friendly status code descriptions if backend provided no custom message
  const status = Number(err?.response?.status) || undefined
  if (status === 400) {
    return fallback !== 'An unexpected error occurred'
      ? fallback
      : 'Invalid request data. Please check your input.'
  }
  if (status === 401) {
    return 'Your session has expired or you are not authorized. Please log in again.'
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.'
  }
  if (status === 404) {
    return 'The requested resource was not found.'
  }
  if (status === 409) {
    return 'Conflict with current resource state. Please refresh and try again.'
  }
  if (status === 422) {
    return 'Validation failed. Please verify the entered data.'
  }
  if (status && status >= 500) {
    return 'Server error occurred while processing the request. Please try again later.'
  }

  // 6. Generic error message (excluding raw Axios status messages)
  const msg = err?.message
  if (
    typeof msg === 'string' &&
    msg.trim() &&
    !msg.startsWith('Request failed with status code')
  ) {
    return msg.trim()
  }

  return fallback
}

/**
 * There are basically 2 types of errors:
 *
 *   `validationErrors` - An object that contains validation errors by fields.
 *   Usually used to display errors under inputs, user entered data.
 *
 *   `error` - Common error with overall text that we just display inside a toast if needed.
 *
 * @param error A catched error
 * @param fallback Optional fallback message
 * @returns {Object} Parsed errors
 */
export function parseErrors<FieldTypes extends string = string>(
  error: unknown,
  fallback?: string
): ParseErrorsOutput<FieldTypes> {
  const err = error as any
  const validationErrors = err?.response?.data?.errors as
    | ValidationError[]
    | undefined

  const parsed: ParseErrorsOutput<FieldTypes> = {
    errorStatus: Number(err?.response?.status) || 0,
    errorCode: String(err?.response?.data?.code || ''),
    error: extractErrorMessage(error, fallback),
  }

  if (validationErrors && validationErrors.length) {
    parsed.validationErrorsRaw = validationErrors
    parsed.validationErrors = parseValidationErrors(validationErrors)
  }

  return parsed
}
